import { z } from 'zod'
import { timestampSchema, uuidSchema } from './common.js'

export const chatRoleSchema = z.enum(['system', 'user', 'assistant', 'tool'])
export type ChatRole = z.infer<typeof chatRoleSchema>

export const chatSessionSchema = z.object({
  id: uuidSchema,
  workspace_id: uuidSchema,
  user_id: uuidSchema,
  title: z.string().nullable(),
  model: z.string(),
  active_skills: z.array(z.string()).default([]),
  pinned_files: z.array(uuidSchema).default([]),
  message_count: z.number().int().nonnegative(),
  total_tokens_in: z.number().int().nonnegative(),
  total_tokens_out: z.number().int().nonnegative(),
  total_cost_eur: z.number().nonnegative(),
  archived: z.boolean(),
  is_auto_title: z.boolean().default(true),
  context_dossier_path: z.string().nullable().default(null),
  last_output_payload: z.unknown().nullable().default(null),
  primary_brand_id: uuidSchema.nullable().default(null),
  secondary_brand_ids: z.array(uuidSchema).default([]),
  created_at: timestampSchema,
  updated_at: timestampSchema,
})
export type ChatSession = z.infer<typeof chatSessionSchema>

export const chatSessionRenameSchema = z.object({
  title: z.string().min(1).max(200),
})
export type ChatSessionRename = z.infer<typeof chatSessionRenameSchema>

export const chatSessionUpdateSchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    model: z.string().min(1).max(200).optional(),
  })
  .refine((data) => data.title !== undefined || data.model !== undefined, {
    message: 'Mindestens title oder model muss angegeben werden',
    path: ['title'],
  })
export type ChatSessionUpdate = z.infer<typeof chatSessionUpdateSchema>

export const slashCommandKindSchema = z.enum([
  'output',
  'wiki',
  'image',
  'research',
  'linkedin',
  'x',
  'newsletter',
  'blog',
  'presentation',
  'data-analysis',
])
export type SlashCommandKind = z.infer<typeof slashCommandKindSchema>

export const slashMetadataSchema = z.object({
  slash_command: slashCommandKindSchema,
  slash_status: z.enum(['pending', 'completed', 'failed']),
  vault_path: z.string().optional(),
  error: z.string().optional(),
})
export type SlashMetadata = z.infer<typeof slashMetadataSchema>

export const chatSessionCreateSchema = z.object({
  title: z.string().max(200).optional(),
  model: z.string().optional(),
  active_skills: z.array(z.string()).optional(),
  primary_brand_id: uuidSchema.optional(),
  secondary_brand_ids: z.array(uuidSchema).max(3).optional(),
})
export type ChatSessionCreate = z.infer<typeof chatSessionCreateSchema>

export const chatSessionBrandSelectionSchema = z.object({
  primary_brand_id: uuidSchema.nullable().optional(),
  secondary_brand_ids: z.array(uuidSchema).max(3).optional(),
})
export type ChatSessionBrandSelection = z.infer<typeof chatSessionBrandSelectionSchema>

export const chatToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('function'),
  function: z.object({
    name: z.string(),
    arguments: z.string(),
  }),
})
export type ChatToolCall = z.infer<typeof chatToolCallSchema>

export const chatAttachmentSchema = z.object({
  id: z.string(),
  filename: z.string(),
  mime: z.string(),
  size_bytes: z.number().int().nonnegative(),
  storage_path: z.string(),
  kind: z.enum(['image', 'office', 'audio', 'pdf', 'pptx', 'text']),
  extracted_text: z.string().optional(),
  transcript: z.string().optional(),
  // Server-enriched short-lived (~1h) URL fuer Inline-Render (B1.5.1).
  // Beim Insert leer; Backend setzt es beim GET /messages.
  signed_url: z.string().optional(),
})
export type ChatAttachment = z.infer<typeof chatAttachmentSchema>

// Persistierte Quellen-Attribution (M2-A): Vault-Snippets, die beim
// Beantworten der Nachricht als Kontext verwendet wurden (EDIH: Quellenangabe).
// Optional gehalten, damit Bestands-Objekte ohne das Feld kompatibel bleiben.
export const chatMessageSourceSchema = z.object({
  path: z.string(),
  title: z.string(),
  updated_at: z.string().nullable().default(null),
})
export type ChatMessageSource = z.infer<typeof chatMessageSourceSchema>

export const chatMessageSchema = z.object({
  id: uuidSchema,
  session_id: uuidSchema,
  workspace_id: uuidSchema,
  role: chatRoleSchema,
  content: z.string(),
  tool_calls: z.array(chatToolCallSchema).nullable(),
  tool_call_id: z.string().nullable(),
  tool_name: z.string().nullable(),
  tokens_in: z.number().int().nonnegative().nullable(),
  tokens_out: z.number().int().nonnegative().nullable(),
  cost_eur: z.number().nonnegative().nullable(),
  model: z.string().nullable(),
  attachments: z.array(chatAttachmentSchema).default([]),
  slash_metadata: slashMetadataSchema.nullable().default(null),
  sources: z.array(chatMessageSourceSchema).nullable().optional(),
  created_at: timestampSchema,
})
export type ChatMessage = z.infer<typeof chatMessageSchema>

export const slashUserMessageSchema = z.object({
  content: z.string().min(1).max(50_000),
  slash_command: slashCommandKindSchema,
})
export type SlashUserMessage = z.infer<typeof slashUserMessageSchema>

export const slashAssistantStubSchema = z.object({
  content: z.string().min(1).max(8000),
  slash_command: slashCommandKindSchema,
  slash_status: z.enum(['completed', 'failed']),
  vault_path: z.string().optional(),
  error: z.string().optional(),
  attachments: z.array(chatAttachmentSchema).optional(),
})
export type SlashAssistantStub = z.infer<typeof slashAssistantStubSchema>

export const chatMessageCreateSchema = z.object({
  role: chatRoleSchema,
  content: z.string().min(1),
  tool_calls: z.array(chatToolCallSchema).optional(),
  tool_call_id: z.string().optional(),
  tool_name: z.string().optional(),
})
export type ChatMessageCreate = z.infer<typeof chatMessageCreateSchema>

export const sendChatMessageSchema = z.object({
  content: z.string().min(1).max(50_000),
  attachments: z.array(chatAttachmentSchema).max(10).optional(),
  search_vault: z.boolean().optional(),
})
export type SendChatMessage = z.infer<typeof sendChatMessageSchema>
